import { of } from 'rxjs';
import { Injectable } from '@angular/core';
import { Router } from '@angular/router';
import { Actions, createEffect, ofType } from '@ngrx/effects';
import { map, catchError, exhaustMap, tap, withLatestFrom } from 'rxjs/operators';
import { AlarmsService } from '../services/alarms.service';
import { AlarmSettingsService } from '../services/alarm-settings.service';

import * as alarmsActions from '../actions/alarms.actions';
import { AlarmsActionTypes } from '../actions/alarms.actions';
import * as fromAlarms from '../reducers';
import { Store } from '@ngrx/store';
import { State } from '../reducers';
import { AlarmSetting, Alarm } from '@model';

@Injectable()
export class AlarmsEffects {
  constructor(
    private router: Router,
    private alarmsService: AlarmsService,
    private alarmSettingsService: AlarmSettingsService,
    private store: Store<State>,
    private actions$: Actions
  ) {}

  loadData$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AlarmsActionTypes.LoadData),
      exhaustMap(action => {
        return this.alarmsService.getAlarms().pipe(
          map(alarms => new alarmsActions.LoadSuccess(alarms)),
          catchError(error => of(new alarmsActions.LoadFail(error)))
        );
      })
    );
  });
  
  createItem$ = createEffect(() => {
    return  this.actions$.pipe(
      ofType(AlarmsActionTypes.CreateItem),
      map((action: alarmsActions.Create) => action.payload),
      exhaustMap(alarm =>
        this.alarmsService.createAlarm(alarm).pipe(
          map(item => new alarmsActions.CreateSuccess(item)),
          catchError(error => of(new alarmsActions.CreateFail(error)))
        )
      )
    );
  });

  createItemSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlarmsActionTypes.CreateItemSuccess),
      map((action: alarmsActions.Create) => action.payload),
      tap(item => this.router.navigate([`/secure/alarms`]))
    ),
    { dispatch: false }
  );
  
  createItems$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AlarmsActionTypes.BulkCreateItems),
      map((action: alarmsActions.BulkCreate) => action.payload),
      exhaustMap(alarms =>
        this.alarmsService.bulkCreateAlarms(alarms).pipe(
          map(items => new alarmsActions.BulkCreateSuccess(items)),
          catchError(error => of(new alarmsActions.BulkCreateFail(error)))
        )
      )
    );
  });

  createItemsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlarmsActionTypes.BulkCreateItemsSuccess),
      map((action: alarmsActions.BulkCreate) => action.payload),
      tap(item => this.router.navigate([`/secure/alarms`]))
    ),
    { dispatch: false }
  );
  
  createItemAndRedirect$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AlarmsActionTypes.CreateItemAndRedirect),
      map((action: alarmsActions.CreateAndRedirect) => action.payload),
      exhaustMap(alarm =>
        this.alarmsService.createAlarm(alarm).pipe(
          map(item => new alarmsActions.CreateAndRedirectSuccess(item)),
          catchError(error => of(new alarmsActions.CreateAndRedirectFail(error)))
        )
      )
    );
  });
  
  createItemAndRedirectSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlarmsActionTypes.CreateItemAndRedirectSuccess),
      map((action: alarmsActions.CreateAndRedirect) => action.payload),
      tap(item => this.router.navigate([`/secure/meteringpoints/` + item.meteringPointId]))
    ),
    { dispatch: false }
  );

  updateAlarm$ = createEffect(() => {
    return  this.actions$.pipe(
      ofType(AlarmsActionTypes.UpdateItem),
      map((action: alarmsActions.Update) => action.payload),
      exhaustMap(alarm =>
        this.alarmsService.updateAlarm(alarm).pipe(
          map(item => new alarmsActions.UpdateSuccess(item)),
          catchError(error => of(new alarmsActions.UpdateFail(error)))
        )
      )
    );
  });
  
 
  updateItemSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlarmsActionTypes.UpdateItemSuccess, AlarmsActionTypes.RemoveItemSuccess),
      tap(() => this.router.navigate([`/secure/alarms`]))
    ),
    { dispatch: false }
  );
  
  removeAlarm$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AlarmsActionTypes.RemoveItem),
      map((action: alarmsActions.Remove) => action.payload),
      exhaustMap(alarm =>
        this.alarmsService.removeAlarm(alarm).pipe(
          map(() => new alarmsActions.RemoveSuccess(alarm)),
          catchError(error => of(new alarmsActions.RemoveFail(error)))
        )
      )
    );
  });
  
  removeAlarmAndRedirect$ = createEffect(() => {
    return  this.actions$.pipe(
      ofType(AlarmsActionTypes.RemoveItemAndRedirect),
      map((action: alarmsActions.RemoveAndRedirect) => action.payload),
      exhaustMap(alarm =>
        this.alarmsService.removeAlarm(alarm).pipe(
          map(() => new alarmsActions.RemoveAndRedirectSuccess(alarm)),
          catchError(error => of(new alarmsActions.RemoveAndRedirectFail(error)))
        )
      )
    );
  });

  removeAlarmAndRedirectSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlarmsActionTypes.RemoveItemAndRedirectSuccess),
      map((action: alarmsActions.RemoveAndRedirect) => action.payload),
      tap(item => this.router.navigate([`/secure/meteringpoints/` + item.meteringPointId]))
    ),
    { dispatch: false }
  );

  removeAlarms$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AlarmsActionTypes.BulkRemoveItems),
      map((action: alarmsActions.BulkRemove) => action.payload),
      exhaustMap(ids =>
        this.alarmsService.bulkRemoveAlarms(ids).pipe(
          map(() => new alarmsActions.BulkRemoveSuccess(ids)),
          catchError(error => of(new alarmsActions.BulkRemoveFail(error)))
        )
      )
    );
  });

  inactivateAlarms$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AlarmsActionTypes.BulkInactivateItems),
      map((action: alarmsActions.BulkInactivate) => action.payload),
      exhaustMap(alarms =>
        this.alarmsService.bulkInactivateAlarms(alarms).pipe(
          map(() => new alarmsActions.BulkInactivateSuccess(alarms)),
          catchError(error => of(new alarmsActions.BulkInactivateFail(error)))
        )
      )
    );
  });

  activateAlarms$ = createEffect(() => {
    return this.actions$.pipe(
      ofType(AlarmsActionTypes.BulkActivateItems),
      map((action: alarmsActions.BulkActivate) => action.payload),
      exhaustMap(alarms =>
        this.alarmsService.bulkActivateAlarms(alarms).pipe(
          map(() => new alarmsActions.BulkActivateSuccess(alarms)),
          catchError(error => of(new alarmsActions.BulkActivateFail(error)))
        )
      )
    );
  });
  
  // Update
  updateAlarmSettings$ = createEffect(() => {
    return  this.actions$.pipe(
      ofType(AlarmsActionTypes.UpdateAlarmSettings),
      map((action: alarmsActions.UpdateAlarmSettings) => action.payload),
      exhaustMap(alarmSettings =>
        this.alarmSettingsService.bulkUpdateAlarmSettings(alarmSettings).pipe(
          map(items => new alarmsActions.UpdateAlarmSettingsSuccess(items)),
          catchError(error => of(new alarmsActions.UpdateAlarmSettingsFail(error)))
        )
      )
    );
  });
  

  // Update and redirect
  updateAlarmSettingsAndRedirectToAlarms$ = createEffect(() => {
    return  this.actions$.pipe(
      ofType(AlarmsActionTypes.UpdateAlarmSettingsAndRedirectToAlarms),
      map((action: alarmsActions.UpdateAlarmSettingsAndRedirectToAlarms) => action.payload),
      exhaustMap(alarmSettings =>
        this.alarmSettingsService.bulkUpdateAlarmSettings(alarmSettings).pipe(
          map(items => new alarmsActions.UpdateAlarmSettingsAndRedirectToAlarmsSuccess(items)),
          catchError(error => of(new alarmsActions.UpdateAlarmSettingsAndRedirectToAlarmsFail(error)))
        )
      )
    );
  });
  updateAlarmSettingsAndRedirectToAlarmsSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlarmsActionTypes.UpdateAlarmSettingsAndRedirectToAlarmsSuccess),
      tap(() => this.router.navigate([`/secure/alarms`]))
    ),
    { dispatch: false }
  );

  updateAlarmSettingsAndRedirect$ = createEffect(() => {
    return  this.actions$.pipe(
      ofType(AlarmsActionTypes.UpdateAlarmSettingsAndRedirect),
      map((action: alarmsActions.UpdateAlarmSettingsAndRedirect) => action.payload),
      exhaustMap(payload =>
        this.alarmSettingsService.bulkUpdateAlarmSettings(payload.alarmSettings).pipe(
          map(items => new alarmsActions.UpdateAlarmSettingsAndRedirectSuccess(items)),
          catchError(error => of(new alarmsActions.UpdateAlarmSettingsAndRedirectFail(error)))
        )
      )
    );
  });
  
  updateAlarmSettingsAndRedirectSuccess$ = createEffect(() =>
    this.actions$.pipe(
      ofType(AlarmsActionTypes.UpdateAlarmSettingsAndRedirectSuccess),
      map((action: alarmsActions.UpdateAlarmSettingsAndRedirect) => action.payload),
      withLatestFrom(this.store.select(fromAlarms.getSelectedAlarm)),
      exhaustMap((data: [{ alarmSettings: AlarmSetting[]; meteringPointId: number }, Alarm]) => {
        let selectedAlarm: Alarm;
        selectedAlarm = data && data[1] ? data[1] : null;

        if (selectedAlarm && selectedAlarm.meteringPointId) {
          return of(selectedAlarm).pipe(
            tap(alarm => this.router.navigate([`/secure/meteringpoints/` + alarm.meteringPointId]))
          );
        } else {
          this.router.navigate([`/secure/meteringpoints`]);
        }
      })
    ),
    { dispatch: false }
  );

  // Bulk Update NotificationChannel on AlarmSettings
  bulkUpdateNotificationChannel$ = createEffect(() => {
    return  this.actions$.pipe(
      ofType(AlarmsActionTypes.BulkUpdateNotificationChannel),
      map((action: alarmsActions.BulkUpdateNotificationChannel) => action.payload),
      exhaustMap(alarmSettings =>
        this.alarmSettingsService.bulkUpdateNotificationChannels(alarmSettings).pipe(
          map(items => new alarmsActions.BulkUpdateNotificationChannelSuccess(items)),
          catchError(error => of(new alarmsActions.BulkUpdateNotificationChannelFail(error)))
        )
      )
    );
  });
}
